import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Trash2, UserPlus, UserMinus, Users } from 'lucide-react';
import { teamApi, userManagementApi } from '../../../services/api';
import { Team, User } from '../../../types';
import LoadingCard from '../../../components/LoadingCard';

interface TeamDetailsPanelProps {
  teamId: number;
  onClose: () => void;
  onUpdate: () => void;
}

const TeamDetailsPanel: React.FC<TeamDetailsPanelProps> = ({ teamId, onClose, onUpdate }) => {
  const [team, setTeam] = useState<Team | null>(null);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<User[]>([]);
  const [showAddMembers, setShowAddMembers] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);

  useEffect(() => {
    loadTeam();
    loadUsers();
  }, [teamId]);

  const loadTeam = async () => {
    try {
      setLoading(true);
      const data = await teamApi.getById(teamId);
      setTeam(data);
    } catch (error) {
      console.error('Failed to load team:', error);
      alert('Failed to load team details');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await userManagementApi.list({ per_page: 100 });
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to load users:', error);
    }
  };

  const handleAddMembers = async () => {
    if (selectedUserIds.length === 0) {
      alert('Please select at least one user');
      return;
    }
    try {
      await teamApi.addMembers(teamId, selectedUserIds);
      setSelectedUserIds([]);
      setShowAddMembers(false);
      loadTeam();
      onUpdate();
    } catch (error) {
      console.error('Failed to add members:', error);
      alert('Failed to add members');
    }
  };

  const handleRemoveMember = async (userId: number) => {
    if (!window.confirm('Remove this member from the team?')) return;
    try {
      await teamApi.removeMembers(teamId, [userId]);
      loadTeam();
      onUpdate();
    } catch (error) {
      console.error('Failed to remove member:', error);
      alert('Failed to remove member');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this team?')) return;
    try {
      await teamApi.delete(teamId);
      onUpdate();
      onClose();
    } catch (error) {
      console.error('Failed to delete team:', error);
      alert('Failed to delete team');
    }
  };

  if (loading) {
    return createPortal(
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999]">
        <div className="max-w-md w-full mx-4">
          <LoadingCard title="Team Details" message="Loading team details..." />
        </div>
      </div>,
      document.body
    );
  }

  if (!team) {
    return null;
  }

  const availableUsers = users.filter(
    user => !team.members?.some(member => member.id === user.id)
  );

  return createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] pt-20 pb-8" onClick={onClose}>
      <div
        className="theme-bg-primary rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[calc(100vh-8rem)] overflow-y-auto theme-border"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 theme-bg-primary border-b theme-border px-4 py-2.5 flex items-center justify-between">
          <h2 className="text-lg font-semibold theme-text-primary">Team Details</h2>
          <button
            onClick={onClose}
            className="p-1 hover:opacity-80 rounded-full transition-opacity"
          >
            <X className="w-4 h-4 theme-text-secondary" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium theme-text-secondary mb-0.5">Team Name</label>
            <p className="text-base font-semibold theme-text-primary">{team.name}</p>
            {team.description && (
              <>
                <label className="block text-sm font-medium theme-text-secondary mb-0.5 mt-3">Description</label>
                <p className="text-sm theme-text-primary">{team.description}</p>
              </>
            )}
          </div>

          <div className="border-t theme-border pt-3">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold theme-text-primary flex items-center gap-1.5">
                <Users className="w-3.5 h-3.5" />
                Members ({team.members?.length || 0})
              </h4>
              {!showAddMembers && (
                <button
                  onClick={() => setShowAddMembers(true)}
                  className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-700"
                >
                  <UserPlus className="w-3.5 h-3.5" />
                  Add Members
                </button>
              )}
            </div>

            {showAddMembers && (
              <div className="mb-3 p-3 theme-card theme-border rounded-lg">
                <div className="mb-2">
                  <label className="block text-sm font-medium theme-text-primary mb-1.5">
                    Select Users to Add
                  </label>
                  <div className="theme-border rounded p-2 max-h-40 overflow-y-auto theme-bg-primary">
                    {availableUsers.length === 0 ? (
                      <p className="text-xs theme-text-tertiary">All users are already members</p>
                    ) : (
                      <div className="space-y-1.5">
                        {availableUsers.map((user) => (
                          <label
                            key={user.id}
                            className="flex items-center gap-2 p-1.5 hover:opacity-80 rounded cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={selectedUserIds.includes(user.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedUserIds([...selectedUserIds, user.id]);
                                } else {
                                  setSelectedUserIds(selectedUserIds.filter(id => id !== user.id));
                                }
                              }}
                              className="w-3.5 h-3.5 text-blue-600 theme-border rounded focus:ring-blue-500"
                            />
                            <div className="flex-1">
                              <div className="text-sm font-medium theme-text-primary">{user.name}</div>
                              <div className="text-xs theme-text-secondary">{user.email}</div>
                            </div>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleAddMembers}
                    disabled={selectedUserIds.length === 0}
                    className="px-3 py-1.5 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Add Selected ({selectedUserIds.length})
                  </button>
                  <button
                    onClick={() => {
                      setShowAddMembers(false);
                      setSelectedUserIds([]);
                    }}
                    className="px-3 py-1.5 theme-border theme-bg-secondary theme-text-primary text-xs rounded hover:opacity-80"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {team.members && team.members.length > 0 ? (
              <div className="space-y-1.5">
                {team.members.map((member) => (
                  <div
                    key={member.id}
                    className="flex items-center justify-between p-2 theme-card theme-border rounded"
                  >
                    <div>
                      <div className="text-sm font-medium theme-text-primary">{member.name}</div>
                      <div className="text-xs theme-text-secondary">{member.email}</div>
                    </div>
                    <button
                      onClick={() => handleRemoveMember(member.id)}
                      className="p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                      title="Remove Member"
                    >
                      <UserMinus className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs theme-text-tertiary">No members in this team</p>
            )}
          </div>

          <div className="border-t theme-border pt-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium theme-text-secondary mb-0.5">Created by</label>
                <p className="text-sm theme-text-primary">{team.creator?.name || 'Unknown'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium theme-text-secondary mb-0.5">Created</label>
                <p className="text-sm theme-text-primary">
                  {team.created_at
                    ? new Date(team.created_at).toLocaleDateString()
                    : 'Unknown'}
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between gap-3 pt-4 border-t theme-border">
            <button
              onClick={handleDelete}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-red-700 bg-red-100 rounded hover:bg-red-200 transition-colors"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Delete Team
            </button>
            <button
              onClick={onClose}
              className="px-3 py-1.5 text-sm theme-border rounded theme-bg-secondary theme-text-primary hover:opacity-80"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default TeamDetailsPanel;

